import React, { useEffect, useState } from "react";
import { Check, Layers } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@libs/zapi-ui/shadcn";
import { AppButton, AppInput, FigmaTypography, isEnterSubmit } from "@libs/zapi-ui/custom";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";

type DimensionLabelProps = {
  dimension: TOrganizationProjectDimension;
  value: string;
  onChange: (id: number, value: string) => void;
  disabled?: boolean;
};

export function DimensionLabel({ dimension, value, onChange, disabled }: DimensionLabelProps) {
  const [open, setOpen] = useState(false);
  const hasValue = !!value.trim();

  const button = (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        "border border-innoveraZinc-150 flex gap-1 h-6 items-center px-2 py-[5px] rounded-md transition-colors",
        disabled ? "text-innoveraZinc-400" : "text-innoveraZinc-900 hover:bg-innoveraZinc-50 cursor-pointer"
      )}
    >
      <Layers className="size-3" />
      <FigmaTypography component="span" variant="label-3">{hasValue ? value : dimension.name}</FigmaTypography>
    </button>
  );

  if (disabled) return button;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{button}</PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className={cn(
          "p-1 rounded-lg shadow-[0px_2px_8px_0px_rgba(0,0,0,0.07)] border border-innoveraZinc-100",
          dimension.type === "dropdown" ? "w-[170px]" : "w-[240px]"
        )}
      >
        {dimension.type === "dropdown" ? (
          <DropdownContent
            dimension={dimension}
            value={value}
            onSelect={(next) => {
              onChange(dimension.id, next);
              setOpen(false);
            }}
          />
        ) : (
          <TextContent
            dimension={dimension}
            value={value}
            onSave={(next) => {
              onChange(dimension.id, next);
              setOpen(false);
            }}
            isOpen={open}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

type DropdownContentProps = {
  dimension: TOrganizationProjectDimension;
  value: string;
  onSelect: (value: string) => void;
};

function DropdownContent({ dimension, value, onSelect }: DropdownContentProps) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[12px] leading-[18px] font-medium text-innoveraZinc-500 px-2 pt-1 pb-0.5">
        {dimension.name}
      </div>
      {dimension.options.map((option) => {
        const isSelected = option === value;
        return (
          <button
            key={option}
            type="button"
            onClick={() => onSelect(option)}
            className={cn(
              "flex items-center gap-2 h-8 px-2 rounded-lg text-left text-innoveraZinc-900",
              "text-[14px] leading-5 font-medium",
              "hover:bg-innoveraZinc-50",
              isSelected && "bg-innoveraZinc-50"
            )}
          >
            <Check className={cn("size-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
            <span className="truncate">{option}</span>
          </button>
        );
      })}
    </div>
  );
}

type TextContentProps = {
  dimension: TOrganizationProjectDimension;
  value: string;
  onSave: (value: string) => void;
  isOpen: boolean;
};

function TextContent({ dimension, value, onSave, isOpen }: TextContentProps) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (isOpen) setDraft(value);
  }, [isOpen, value]);

  const trimmedDraft = draft.trim();
  const canSave = trimmedDraft.length > 0;

  return (
    <div className="flex flex-col gap-2 p-1">
      <div className="text-[12px] leading-[18px] font-medium text-innoveraZinc-500 px-1">
        {dimension.name}
      </div>
      <div className="flex items-center gap-2">
        <AppInput
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (isEnterSubmit(e, { allowShift: true }) && canSave) onSave(trimmedDraft);
          }}
          fullWidth
        />
        <AppButton onClick={() => canSave && onSave(trimmedDraft)} disabled={!canSave}>
          Save
        </AppButton>
      </div>
    </div>
  );
}
